import type {
  PortalObjectPresentation,
  PortalObjectKind,
} from '@terra/shared-dto-chat-types';
import type { ChatObjectNavigationTarget } from '../components/hitl-types';

export function readPortalObjectPresentation(
  output: unknown,
  input: unknown,
): PortalObjectPresentation | null {
  return readPayload(output) ?? readPayload(input);
}

export function portalObjectPresentationToNavigationTarget(
  presentation: PortalObjectPresentation,
): ChatObjectNavigationTarget | null {
  switch (presentation.objectKind) {
    case 'Finding':
      return {
        type: 'finding',
        id: presentation.objectId,
        label: presentation.title,
        mode: 'view',
      };
    case 'Signal':
      return {
        type: 'signal',
        id: presentation.objectId,
        label: presentation.title,
      };
    case 'EndPoint':
      return {
        type: 'endpoint',
        id: presentation.objectId,
        label: presentation.title,
        endpointUrl: presentation.endpointUrl,
      };
    case 'WebPage':
      return {
        type: 'webpage',
        id: presentation.objectId,
        label: presentation.title,
      };
    case 'Trace':
      return {
        type: 'execution',
        id: presentation.objectId,
        label: presentation.title,
      };
    case 'SkillDraft':
      if (!presentation.skillDraftMode) {
        return null;
      }
      return {
        type: 'skill-draft',
        id: presentation.objectId,
        label: presentation.title,
        mode: presentation.skillDraftMode,
        skillId: presentation.skillId,
      };
    default:
      return assertNever(presentation.objectKind);
  }
}

export function portalObjectKindToCardType(
  objectKind: PortalObjectKind,
): PortalObjectReferenceCardType {
  switch (objectKind) {
    case 'Finding':
      return 'finding';
    case 'Signal':
      return 'signal';
    case 'EndPoint':
      return 'endpoint';
    case 'WebPage':
      return 'webpage';
    case 'Trace':
      return 'execution';
    case 'SkillDraft':
      return 'skill-draft';
    default:
      return assertNever(objectKind);
  }
}

export type PortalObjectReferenceCardType =
  | 'finding'
  | 'signal'
  | 'endpoint'
  | 'webpage'
  | 'execution'
  | 'skill-draft';

const MAX_PAYLOAD_PARSE_DEPTH = 8;

const PORTAL_OBJECT_KINDS: ReadonlySet<PortalObjectKind> = new Set([
  'Finding',
  'Signal',
  'EndPoint',
  'WebPage',
  'Trace',
  'SkillDraft',
]);

function isPortalObjectKind(value: unknown): value is PortalObjectKind {
  return (
    typeof value === 'string' &&
    PORTAL_OBJECT_KINDS.has(value as PortalObjectKind)
  );
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function toPortalObjectPresentation(
  record: Record<string, unknown>,
): PortalObjectPresentation | null {
  if (record.type !== 'portal_object_presentation') {
    return null;
  }
  const objectKind = record.objectKind;
  const objectId = readOptionalString(record.objectId);
  const title = readOptionalString(record.title);
  if (!isPortalObjectKind(objectKind) || !objectId || !title) {
    return null;
  }
  const skillDraftMode =
    record.skillDraftMode === 'create' || record.skillDraftMode === 'edit'
      ? record.skillDraftMode
      : undefined;
  return {
    type: 'portal_object_presentation',
    objectKind,
    objectId,
    title,
    reason: readOptionalString(record.reason),
    endpointUrl: readOptionalString(record.endpointUrl),
    skillDraftMode,
    skillId: readOptionalString(record.skillId),
  };
}

function readPayload(
  value: unknown,
  depth = 0,
): PortalObjectPresentation | null {
  if (depth > MAX_PAYLOAD_PARSE_DEPTH) {
    return null;
  }
  const parsed = typeof value === 'string' ? safeJsonParse(value) : value;
  if (!parsed || typeof parsed !== 'object') {
    return null;
  }
  const record = parsed as Record<string, unknown>;
  const direct = toPortalObjectPresentation(record);
  if (direct) {
    return direct;
  }
  const nested = record.output ?? record.result ?? record.content;
  if (nested && nested !== parsed) {
    return readPayload(nested, depth + 1);
  }
  return null;
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function assertNever(value: never): never {
  throw new Error(`Unhandled portal object kind: ${String(value)}`);
}
